// /admin/reset-password — pantalla a la que se llega desde el email recovery
// de Supabase. Parsea el hash de la URL para extraer access_token, refresh_token
// y type. Si type==='recovery' y access_token existe, muestra formulario de
// nueva contraseña. Submit → POST /api/auth/reset-password.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

import Logo from '../components/Logo';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../lib/api';

function parseHashTokens() {
  if (typeof window === 'undefined') return {};
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  if (!hash) return {};
  const params = new URLSearchParams(hash);
  return {
    access_token: params.get('access_token') || null,
    refresh_token: params.get('refresh_token') || null,
    type: params.get('type') || null,
    error: params.get('error') || null,
    error_description: params.get('error_description') || null,
    expires_in: params.get('expires_in') || null,
  };
}

function checkPasswordPolicy(p) {
  return {
    length: p.length >= 12,
    upper: /[A-Z]/.test(p),
    lower: /[a-z]/.test(p),
    digit: /[0-9]/.test(p),
    symbol: /[^A-Za-z0-9]/.test(p),
  };
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const tokens = useMemo(() => parseHashTokens(), []);
  const linkValido = tokens.type === 'recovery' && !!tokens.access_token;

  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState(null);
  const [hecho, setHecho] = useState(false);

  // Si el hash trae error explícito (ej. link expirado), Supabase manda
  // ?error=access_denied&error_description=Email+link+is+invalid+or+has+expired
  useEffect(() => {
    if (tokens.error) {
      setErrorGeneral(t('admin.reset.error_link', { defaultValue: tokens.error_description || tokens.error }));
    }
  }, [tokens.error, tokens.error_description, t]);

  const policy = checkPasswordPolicy(password);
  const policyOk = policy.length && policy.upper && policy.lower && policy.digit && policy.symbol;
  const matches = password.length > 0 && password === password2;

  const submit = async (e) => {
    e.preventDefault();
    setErrorGeneral(null);
    if (!policyOk) {
      setErrorGeneral(t('admin.reset.error_weak'));
      return;
    }
    if (!matches) {
      setErrorGeneral(t('admin.reset.error_mismatch'));
      return;
    }
    setEnviando(true);
    try {
      await resetPassword({ access_token: tokens.access_token, password });
      setHecho(true);
      // Limpia el hash para que un refresh no reuse el token (probablemente ya consumido server-side)
      try { window.history.replaceState(null, '', window.location.pathname); } catch (_) {}
      setTimeout(() => {
        navigate('/login?msg=password_reset_ok', { replace: true });
      }, 2200);
    } catch (err) {
      if (err instanceof ApiError) {
        const traduccion = t('admin.reset.errors.' + err.code, { defaultValue: '' });
        setErrorGeneral(traduccion || err.message || t('auth.errores.generico'));
      } else {
        setErrorGeneral(t('auth.errores.generico'));
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <Link to="/" className="login-logo"><Logo /></Link>
        <h1 className="login-titulo">{t('admin.reset.title')}</h1>

        {!linkValido ? (
          <div className="login-error-block">
            <AlertCircle size={32} strokeWidth={1.5} />
            <p>{errorGeneral || t('admin.reset.link_invalido')}</p>
            <p className="login-volver">
              <Link to="/admin/forgot-password">{t('admin.reset.pedir_otro')}</Link>
            </p>
          </div>
        ) : hecho ? (
          <div className="login-success">
            <CheckCircle2 size={32} strokeWidth={1.5} />
            <p>{t('admin.reset.success')}</p>
          </div>
        ) : (
          <form className="login-form" onSubmit={submit} noValidate>
            <div className="modal-campo">
              <label htmlFor="rp-password">{t('admin.reset.new_password')}</label>
              <input
                id="rp-password"
                type="password"
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={enviando}
              />
            </div>

            <div className="modal-campo">
              <label htmlFor="rp-password2">{t('admin.reset.confirm_password')}</label>
              <input
                id="rp-password2"
                type="password"
                autoComplete="new-password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                disabled={enviando}
              />
            </div>

            <ul className="login-policy">
              <li className={policy.length ? 'ok' : ''}>{t('admin.reset.req_length')}</li>
              <li className={policy.upper ? 'ok' : ''}>{t('admin.reset.req_upper')}</li>
              <li className={policy.lower ? 'ok' : ''}>{t('admin.reset.req_lower')}</li>
              <li className={policy.digit ? 'ok' : ''}>{t('admin.reset.req_digit')}</li>
              <li className={policy.symbol ? 'ok' : ''}>{t('admin.reset.req_symbol')}</li>
            </ul>

            {errorGeneral && (
              <div className="modal-error-general">
                <AlertCircle size={16} strokeWidth={1.75} />
                <span>{errorGeneral}</span>
              </div>
            )}

            <button type="submit" className="btn-primary login-btn" disabled={enviando || !policyOk || !matches}>
              {enviando ? (
                <><Loader2 size={16} className="spin" strokeWidth={1.5} /> {t('auth.enviando')}</>
              ) : (
                t('admin.reset.submit')
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
